import type { MegaJSON, Change } from "../types";
import { ComponentKey } from "../constants";
import { mutate } from "../state/stateMutators";

type CreateRemoveRuleChangesReturnType = Change[];

/** This function creates changes to remove a rule and its corresponding condition groups and conditions
 * @param ruleId - The id of the rule to be removed
 * @param process - The process ( meta-json ) from which the rule is to be removed
 * Here StudioCoreProcess is in-memory representation of meta-json and Process is representing the old studio process
 * @returns
 */

export const createRemoveRulesChanges = ({
  processId,
  ruleId,
  megaJSON
}: {
  processId: string;
  ruleId: string;
  megaJSON: MegaJSON;
}): CreateRemoveRuleChangesReturnType => {
  if (!ruleId.length) return [];

  const changes: Change[] = [];

  const conditionGroupsToDelete: string[] = [];
  const conditionsToDelete: string[] = [];
  const process = megaJSON.processes[processId];

  // Functions that recursively find all nested conditionGroups from a conditionGroupId
  const getNestedConditionGroups = (conditionGroupId: string) => {
    const conditionGroupData = process.conditionGroups[conditionGroupId];
    if (conditionGroupData?.groups && conditionGroupData.groups.length > 0) {
      conditionGroupsToDelete.push(...conditionGroupData.groups);
      conditionGroupData.groups.forEach(getNestedConditionGroups);
    }
  };

  // Finding groupIds to delete
  const rule = process.rules[ruleId];
  if (rule?.topLevelGroupId) {
    conditionGroupsToDelete.push(rule.topLevelGroupId);
    getNestedConditionGroups(rule.topLevelGroupId);
  }

  // Finding conditionIds to delete
  conditionGroupsToDelete.forEach(cdGroupId => {
    const conditionGroupData = process.conditionGroups[cdGroupId];
    if (
      conditionGroupData?.conditions &&
      conditionGroupData.conditions.length > 0
    ) {
      conditionsToDelete.push(...conditionGroupData.conditions);
    }
  });

  // Deleting the rule and its corresponding condition groups and conditions
  changes.push(
    mutate.remove({
      state: megaJSON,
      processId,
      componentKey: ComponentKey.RULES,
      component: process.rules[ruleId]
    })
  );

  conditionGroupsToDelete.forEach(cdGroupId => {
    changes.push(
      mutate.remove({
        state: megaJSON,
        processId,
        componentKey: ComponentKey.CONDITION_GROUPS,
        component: process.conditionGroups[cdGroupId]
      })
    );
  });

  conditionsToDelete.forEach(conditionId => {
    changes.push(
      mutate.remove({
        state: megaJSON,
        processId,
        componentKey: ComponentKey.CONDITIONS,
        component: process.conditions[conditionId]
      })
    );
  });

  return changes;
};
